import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { organizationSchema, productSchema } from '@/lib/seo';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { BestDealBanner } from '@/components/BestDealBanner';
import { StickyAffiliateCTA } from '@/components/StickyAffiliateCTA';
import PropFirmComparisonTable from '@/components/Reviews/PropFirmComparisonTable';
import { EmailSubscribe } from '@/components/EmailSubscribe';
import { PropFirmWithPrograms } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ErrorBoundary } from '@/components/ui/error-boundary';
import { getVerificationState, getCtaLabel, hasVerifiedDiscount } from '@/utils/verification';
import { useAffiliateClick } from '@/hooks/useAffiliateClick';

// SEO-focused keyword lists
const seoKeywords = {
  primary: 'futures prop trading firms, best futures prop firms, futures trading funded accounts',
  secondary: 'proprietary trading firms for futures, futures trading evaluation programs, funded futures trading',
  longTail: 'how to compare futures prop firms, best profit split prop trading firms, cheapest futures trading evaluations'
};

const Compare = () => {
  const [firms, setFirms] = useState<PropFirmWithPrograms[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { trackClick } = useAffiliateClick();

  // Generate Schema.org structured data
  const comparisonSchemaData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": ["h1", "h2", ".description"]
    },
    "name": "Compare Futures Prop Trading Firms - Find the Best Funded Account",
    "description": "Compare the best proprietary trading firms for futures traders. Filter by account size, profit split, evaluation phases and find exclusive discounts.",
    "isPartOf": {
      "@type": "WebSite",
      "name": "PinnacleBuy",
      "url": "https://pinnaclebuy.com"
    },
    "about": {
      "@type": "Thing",
      "name": "Futures Proprietary Trading",
      "description": "Funded futures trading accounts provided by proprietary trading firms"
    }
  };

  useEffect(() => {
    const fetchPropFirms = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching prop firms data...');

        if (!supabase) {
          throw new Error('Database client not initialized');
        }

        const { data, error: supabaseError } = await supabase
          .from('prop_firms')
          .select('*, funding_programs(*)')
          .eq('is_active', true)
          .order('display_order', { ascending: true });

        if (supabaseError) {
          console.error('Supabase error:', supabaseError);
          throw new Error('Failed to fetch data from the database');
        }

        if (!data) {
          throw new Error('No data received from the database');
        }

        const firmsWithDefaults = (data as PropFirmWithPrograms[]).map(firm => ({
          ...firm,
          funding_programs: Array.isArray(firm.funding_programs) ? firm.funding_programs : [],
        }));

        setFirms(firmsWithDefaults);
      } catch (error) {
        console.error('Error fetching prop firms:', error);
        setError(error instanceof Error ? error.message : 'Failed to load prop firms. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchPropFirms();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg mx-4">
          <h2 className="text-red-800 font-semibold mb-2">Error Loading Data</h2>
          <p className="text-red-600">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg mx-4">
            <h2 className="text-red-800 font-semibold mb-2">Something went wrong</h2>
            <p className="text-red-600">We encountered an error while rendering the comparison table.</p>
            <button 
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      }
    >
      <div className="min-h-screen flex flex-col pb-20 lg:pb-0">
        <Helmet>
          <title>Compare Futures Prop Trading Firms | Find the Best Funded Account</title>
          <meta name="description" content="Compare the best proprietary trading firms for futures trading. Filter by account size, profit split, evaluation phases and find exclusive discounts." />
          <meta name="keywords" content={`${seoKeywords.primary}, ${seoKeywords.secondary}`} />
          
          {/* Open Graph / Facebook */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content="Compare Futures Prop Trading Firms | Find the Best Funded Account" />
          <meta property="og:description" content="Compare the best proprietary trading firms for futures trading. Filter by account size, profit split, evaluation phases and find exclusive discounts." />
          <meta property="og:url" content="https://pinnaclebuy.com/compare" />
          <meta property="og:image" content="https://pinnaclebuy.com/og-image.png" />

          {/* Twitter */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Compare Futures Prop Trading Firms | Find the Best Funded Account" />
          <meta name="twitter:description" content="Compare the best proprietary trading firms for futures trading. Filter by account size, profit split, evaluation phases and find exclusive discounts." />
          <meta name="twitter:image" content="https://pinnaclebuy.com/og-image.png" />
          
          {/* Canonical URL */}
          <link rel="canonical" href="https://pinnaclebuy.com/compare" />
          
          {/* Schema.org JSON-LD structured data */}
          <script type="application/ld+json">
            {JSON.stringify(comparisonSchemaData)}
          </script>
          <script type="application/ld+json">
            {JSON.stringify(organizationSchema())}
          </script>
          {firms.length > 0 && (
            <script type="application/ld+json">
              {JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: 'Futures Prop Trading Firms Comparison',
                itemListOrder: 'https://schema.org/ItemListOrderDescending',
                numberOfItems: firms.length,
                itemListElement: firms.map((firm, index) => {
                  const offers = firm.funding_programs
                    .map(p => p.discounted_price ?? p.base_price)
                    .filter((p): p is number => p != null)
                    .map(price => ({ price }));
                  return {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: productSchema({
                      name: firm.name,
                      description: firm.notable_features || `${firm.name} futures prop trading firm`,
                      url: `/firms/${firm.slug}`,
                      offers,
                    }),
                  };
                }),
              })}
            </script>
          )}
        </Helmet>

        <Header />

        <main className="flex-grow bg-gray-50 dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-800">
          {/* Page Header with SEO-optimized H1 */}
          <div className="bg-white border-b border-gray-200 dark:bg-gray-800/80 dark:border-gray-700/50">
            <div className="container mx-auto px-4 py-6">
              <h1 className="text-3xl font-bold text-gray-900 mb-1 dark:text-white">Compare Futures Prop Trading Firms</h1>
              <p className="text-gray-600 dark:text-gray-300 description">
                Find the perfect proprietary trading firm for your futures trading style. Filter by account size, profit split, evaluation phases, and more to discover your ideal funded account.
              </p>
              {!loading && firms.length > 0 && (() => {
                const mostRecent = firms
                  .map(f => f.last_verified_at)
                  .filter((d): d is string => d != null)
                  .sort()
                  .pop();
                return mostRecent ? (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Data last verified: {new Date(mostRecent).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                    Verification dates vary by firm
                  </p>
                );
              })()}
            </div>
          </div>

          {/* Best Deal Banner */}
          <div className="container mx-auto px-4 py-4">
            <BestDealBanner />
          </div>

          {/* Stale-data advisory — shown when any firm has stale or unverified data */}
          {!loading && firms.length > 0 && (() => {
            const staleCount = firms.filter(f => getVerificationState(f.last_verified_at) !== 'verified').length;
            if (staleCount === 0) return null;
            return (
              <div className="container mx-auto px-4 pt-2">
                <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {staleCount} of {firms.length} firms have not been re-verified in the last 14 days. Rows with stale or unverified data are marked accordingly. Always confirm pricing on the firm's official site before purchasing.
                  </p>
                </div>
              </div>
            );
          })()}

          {/* Show the PropFirmComparisonTable with data from the database */}
          <div className="container mx-auto px-4 py-4">
            <PropFirmComparisonTable firms={firms} loading={loading} />
          </div>

          {/* Combined information box - with content from both sections */}
          <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg p-5 shadow-sm dark:bg-gray-800/80 dark:border dark:border-gray-700/30">
              <h2 className="text-xl font-semibold mb-2 dark:text-white">Compare Top Futures Prop Trading Firms</h2>
              <p className="text-gray-700 mb-3 dark:text-gray-300">
                Unlock access to capital and take your trading to the next level with the best proprietary futures trading firms. Our powerful comparison tool helps you find the right fit by breaking down account options, rules, profit splits, and more — so you can focus on what matters: trading profitably.
              </p>
              <p className="text-gray-700 mb-4 dark:text-gray-300">
                Whether you're scalping the micro contracts or swinging the majors, we help you filter by trading style, risk tolerance, and evaluation criteria to discover the most competitive offers in the prop firm space. Many firms support popular contracts like ES, NQ, MES, MNQ, CL, and more — with funding offers starting under $50.
              </p>
              
              <h3 className="font-medium text-lg mb-2 dark:text-gray-200">What to Look for in a Futures Prop Firm</h3>
              <p className="text-gray-700 mb-3 dark:text-gray-300">
                Before committing to a firm, consider these critical features that can affect your profitability and trading experience:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <ul className="list-disc pl-5 space-y-1.5 text-gray-700 dark:text-gray-300">
                    <li><strong>Evaluation Model:</strong> One-step vs. two-step challenges, and whether there's a reset fee or retry period.</li>
                    <li><strong>Profit Targets:</strong> Typically 8% to 15% — higher targets may increase difficulty.</li>
                    <li><strong>Risk Rules:</strong> Daily loss limits, max drawdown, trailing vs. static drawdown.</li>
                    <li><strong>Trading Rules:</strong> Some firms restrict news trading, holding over weekends, or high-frequency strategies.</li>
                    <li><strong>Profit Splits:</strong> Vary widely — from 50% up to 90% depending on the firm and account type.</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-base mb-1.5 dark:text-gray-200">Match Your Strategy to the Right Firm</h4>
                  <p className="text-gray-700 mb-1.5 dark:text-gray-300">
                    Use our filters to find firms that support your specific trading needs:
                  </p>
                  <ul className="list-disc pl-5 space-y-1.5 text-gray-700 dark:text-gray-300">
                    <li><strong>Day Traders:</strong> Favor firms with tight drawdowns and fast evaluation periods.</li>
                    <li><strong>Swing Traders:</strong> Look for overnight and weekend holding permissions.</li>
                    <li><strong>Scalpers:</strong> Seek firms with minimal trade count requirements and quick fills.</li>
                    <li><strong>News/Event Traders:</strong> Choose firms that explicitly allow trading during economic news releases.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Browse by Category — internal links to Best Of & category pages */}
          <div className="container mx-auto px-4 py-6">
            <div className="bg-white rounded-lg p-5 shadow-sm dark:bg-gray-800/80 dark:border dark:border-gray-700/30">
              <h2 className="text-xl font-semibold mb-4 dark:text-white">Browse by Category</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <h3 className="font-medium text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">By Account Size</h3>
                  <ul className="space-y-1.5">
                    <li><Link to="/best/25k-funded-account" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Best 25K Funded Accounts</Link></li>
                    <li><Link to="/best/50k-funded-account" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Best 50K Funded Accounts</Link></li>
                    <li><Link to="/best/100k-funded-account" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Best 100K Funded Accounts</Link></li>
                    <li><Link to="/best/150k-funded-account" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Best 150K Funded Accounts</Link></li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">By Drawdown Type</h3>
                  <ul className="space-y-1.5">
                    <li><Link to="/best/eod-drawdown-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">EOD Drawdown Firms</Link></li>
                    <li><Link to="/best/trailing-drawdown-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Trailing Drawdown Firms</Link></li>
                    <li><Link to="/best/static-drawdown-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Static Drawdown Firms</Link></li>
                    <li><Link to="/tools/drawdown-simulator" className="text-brand-blue dark:text-blue-400 hover:underline text-sm flex items-center gap-1">Drawdown Simulator Tool <ArrowRight className="h-3 w-3" /></Link></li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">By Feature</h3>
                  <ul className="space-y-1.5">
                    <li><Link to="/best/cheapest-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Cheapest Prop Firms</Link></li>
                    <li><Link to="/best/highest-profit-split-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">Highest Profit Split Firms</Link></li>
                    <li><Link to="/best/no-activation-fee-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">No Activation Fee Firms</Link></li>
                    <li><Link to="/best/no-consistency-rule-prop-firms" className="text-brand-blue dark:text-blue-400 hover:underline text-sm">No Consistency Rule Firms</Link></li>
                  </ul>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700/50 flex flex-wrap gap-4 text-sm">
                <Link to="/blog" className="text-brand-blue dark:text-blue-400 hover:underline flex items-center gap-1">Trading Guides & Blog <ArrowRight className="h-3 w-3" /></Link>
                <Link to="/methodology" className="text-brand-blue dark:text-blue-400 hover:underline flex items-center gap-1">Our Methodology <ArrowRight className="h-3 w-3" /></Link>
                <Link to="/category/beginners" className="text-brand-blue dark:text-blue-400 hover:underline flex items-center gap-1">Beginner Traders <ArrowRight className="h-3 w-3" /></Link>
                <Link to="/category/scalpers" className="text-brand-blue dark:text-blue-400 hover:underline flex items-center gap-1">Scalpers <ArrowRight className="h-3 w-3" /></Link>
                <Link to="/category/swing-traders" className="text-brand-blue dark:text-blue-400 hover:underline flex items-center gap-1">Swing Traders <ArrowRight className="h-3 w-3" /></Link>
              </div>
            </div>
          </div>

          {/* Email Subscription */}
          <div className="container mx-auto px-4 py-8 bg-gradient-to-r from-transparent via-blue-50/30 to-transparent rounded-lg my-4 dark:from-transparent dark:via-blue-900/10 dark:to-transparent">
            <div className="max-w-md mx-auto">
              <EmailSubscribe />
            </div>
          </div>
        </main>

        <Footer />

        {(() => {
          const topDealFirm = firms.find(f => f.affiliate_url);
          if (!topDealFirm) return null;
          return (
            <StickyAffiliateCTA
              firmName={topDealFirm.name}
              ctaLabel={getCtaLabel(
                !!topDealFirm.affiliate_url,
                hasVerifiedDiscount(
                  topDealFirm.funding_programs?.[0]?.base_price ?? null,
                  topDealFirm.funding_programs?.[0]?.discounted_price ?? null,
                  topDealFirm.last_verified_at
                )
              )}
              targetUrl={topDealFirm.affiliate_url || topDealFirm.website_url || '#'}
              couponCode={topDealFirm.coupon_code}
              discountDescription={topDealFirm.discount_description}
              onCTAClick={() => {
                trackClick({
                  firmName: topDealFirm.name,
                  accountSize: 'all',
                  discountCode: topDealFirm.coupon_code || null,
                  affiliateUrl: topDealFirm.affiliate_url || topDealFirm.website_url || '#',
                  source: 'compare_sticky_cta',
                  skipNavigation: true,
                });
              }}
            />
          );
        })()}
      </div>
    </ErrorBoundary>
  );
};

export default Compare;
